import React, { useState } from "react";
import DashboardModal from "../components/DashboardModal";
import MaskedDateInput from "../components/MaskedDateInput";
import { updatePropertyValue } from "../services/ownerPatrimonyService";
import { IPropertyReturn } from "../types/ownerDashboard.types";
import {
  formatPropertyReturnDate,
  getPropertyReturnReferenceDate,
  toDateInputValue,
} from "../utils/formatPropertyReturnDate";
import {
  isoDateToMaskedDisplay,
  maskedDisplayToIsoDate,
} from "../utils/maskedDate";
import { parseCurrencyInput } from "../utils/parseCurrencyInput";

interface UpdatePurchaseDateModalProps {
  propertyReturn: IPropertyReturn;
  propertyLabel: string;
  onClose: () => void;
  onSuccess: () => void;
}

export default function UpdatePurchaseDateModal({
  propertyReturn,
  propertyLabel,
  onClose,
  onSuccess,
}: UpdatePurchaseDateModalProps) {
  const referenceDate = getPropertyReturnReferenceDate(
    propertyReturn.purchase_date,
    propertyReturn.valuation_since_date,
    propertyReturn.purchase_value_registered_at
  );
  const [purchaseDateDisplay, setPurchaseDateDisplay] = useState(
    isoDateToMaskedDisplay(
      toDateInputValue(propertyReturn.purchase_date ?? referenceDate.date)
    )
  );
  const [purchaseValue, setPurchaseValue] = useState(
    String(propertyReturn.purchase_value)
  );
  const [marketValue, setMarketValue] = useState(
    propertyReturn.market_value != null ? String(propertyReturn.market_value) : ""
  );
  const [clearMarketValue, setClearMarketValue] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    const purchaseDate = maskedDisplayToIsoDate(purchaseDateDisplay);

    if (!purchaseDate) {
      setError("Informe uma data de aquisição válida no formato dd/mm/aaaa.");
      return;
    }

    const parsedPurchaseValue = parseCurrencyInput(purchaseValue.trim());

    if (parsedPurchaseValue == null || parsedPurchaseValue <= 0) {
      setError("Informe um valor de aquisição válido.");
      return;
    }

    const trimmedMarketValue = marketValue.trim();
    const parsedMarketValue =
      !clearMarketValue && trimmedMarketValue.length > 0
        ? parseCurrencyInput(trimmedMarketValue)
        : null;

    if (
      !clearMarketValue &&
      trimmedMarketValue.length > 0 &&
      (parsedMarketValue == null || parsedMarketValue <= 0)
    ) {
      setError("Informe um valor de mercado válido ou deixe o campo em branco.");
      return;
    }

    setIsSubmitting(true);

    try {
      const payload: {
        value: number;
        purchase_date: string;
        market_value?: number | null;
      } = {
        value: parsedPurchaseValue,
        purchase_date: purchaseDate,
      };

      if (clearMarketValue) {
        payload.market_value = null;
      } else if (parsedMarketValue != null) {
        payload.market_value = parsedMarketValue;
      }

      await updatePropertyValue(propertyReturn.id, payload);
      await onSuccess();
      onClose();
    } catch {
      setError("Não foi possível atualizar os dados do imóvel. Tente novamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DashboardModal title="Atualizar dados do imóvel" onClose={onClose}>
      <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
        <div className="rounded-xl border border-white/[0.08] bg-[#0d1117] px-3 py-2.5">
          <p className="text-sm font-semibold text-[#f0f6fc]">{propertyLabel}</p>
          <p className="mt-1 text-xs text-[#6e7681]">
            Referência atual: {referenceDate.label.toLowerCase()}{" "}
            {formatPropertyReturnDate(referenceDate.date)}
          </p>
        </div>

        <p className="text-sm text-[#8b949e]">
          Ajuste o valor e a data de aquisição. A valorização FIPE Zap será recalculada a
          partir desses dados. O valor de mercado opcional alimenta o rendimento “sobre valor
          de mercado”.
        </p>

        <label className="flex flex-col gap-1.5 text-sm text-[#8b949e]">
          Data de aquisição
          <MaskedDateInput
            value={purchaseDateDisplay}
            onChange={setPurchaseDateDisplay}
            required
          />
        </label>

        <label className="flex flex-col gap-1.5 text-sm text-[#8b949e]">
          Valor de aquisição (R$)
          <input
            type="text"
            inputMode="decimal"
            placeholder="215500"
            value={purchaseValue}
            onChange={(event) => setPurchaseValue(event.target.value)}
            className="rounded-xl border border-white/[0.08] bg-[#0d1117] px-3 py-2.5 text-sm text-[#f0f6fc] outline-none focus:border-[#6ea8ff]/50"
          />
        </label>

        <label className="flex flex-col gap-1.5 text-sm text-[#8b949e]">
          Valor de mercado (R$) — opcional
          <input
            type="text"
            inputMode="decimal"
            placeholder="320000"
            value={clearMarketValue ? "" : marketValue}
            disabled={clearMarketValue}
            onChange={(event) => setMarketValue(event.target.value)}
            className="rounded-xl border border-white/[0.08] bg-[#0d1117] px-3 py-2.5 text-sm text-[#f0f6fc] outline-none focus:border-[#6ea8ff]/50 disabled:opacity-50"
          />
        </label>

        <label className="flex items-center gap-2 text-xs text-[#8b949e]">
          <input
            type="checkbox"
            checked={clearMarketValue}
            onChange={(event) => setClearMarketValue(event.target.checked)}
            className="h-4 w-4 rounded border-white/20 bg-[#0d1117]"
          />
          Remover valor de mercado cadastrado (voltar à estimativa FIPE)
        </label>

        {error && <p className="text-sm text-[#f85149]">{error}</p>}

        <button
          type="submit"
          disabled={isSubmitting}
          className="rounded-full bg-[#6ea8ff] px-4 py-2.5 text-sm font-bold text-[#0d1117] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSubmitting ? "Salvando..." : "Salvar alterações"}
        </button>
      </form>
    </DashboardModal>
  );
}
